import { z, ZodTypeAny } from "zod";
import type { QuizQuestionSet as QuizQuestionSetType } from "@core/features/quiz";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { Loader } from "@themes/pollinator/ui/Loader";
import { QuizQuestionSet } from "../../components/QuestionSet";

export type Props = {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Whether the quiz is loading */
  isLoading: boolean;
  /** Handler for closing the dialog */
  onClose: () => void;
  /** The current active question set to display */
  activeQuestionSet: QuizQuestionSetType;
  /** Label for the submit button */
  buttonLabel: string;
  /** Filter function for questions in the set */
  filterQuestionSet: (
    question: QuizQuestionSetType["questions"][number],
    index: number
  ) => boolean;
  /** Handler for navigating to next question */
  onNext: (data: z.infer<ZodTypeAny>) => Promise<void>;
  /** Handler for navigating to previous question */
  onBack: () => void;
  /** Whether the quiz is currently submitting */
  isSubmitting: boolean;
  /** Quiz title */
  title: string;
  /** Quiz description */
  description: string;
  /** Form default values */
  formData: Record<string, unknown>;
  /** Current step index (0-based) */
  stepIndex: number;
  /** Total number of steps */
  totalSteps: number;
};

/**
 * Quiz dialog component.
 * Displays a quiz inside a dialog modal.
 *
 * This is a pure presentational component - all data and callbacks
 * should be passed via props from QuizDialogContainer.
 */
export function QuizDialog({
  isOpen,
  isLoading,
  onClose,
  activeQuestionSet,
  buttonLabel,
  filterQuestionSet,
  onNext,
  onBack,
  isSubmitting,
  title,
  description,
  formData,
  stepIndex,
  totalSteps,
}: Props) {
  if (isLoading) {
    return (
      <Dialog
        isOpen={isOpen}
        title="Loading"
        description="Please wait while we load the form."
        onClose={onClose}
        className="flex flex-col flex-1 p-0 md:p-0"
        isWithActions={false}
        size="xl"
      >
        <Loader />
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      title={title}
      description={description}
      onClose={onClose}
      className="flex flex-col flex-1 p-0 md:p-0"
      isWithActions={false}
      size="xl"
    >
      <QuizQuestionSet
        questionSet={activeQuestionSet}
        filterQuestionSet={filterQuestionSet}
        buttonLabel={buttonLabel}
        isSubmitting={isSubmitting}
        onSubmit={onNext}
        onBack={onBack}
        defaultValues={formData}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
      />
    </Dialog>
  );
}

export default QuizDialog;
